import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { coverageMetrics, LAWSUITS_MAX, PAYOUT_MAX } from '../config/coverage-metrics';
import type { CoverageCategoryKey } from '../types';
import coins from '../assets/coverage/coins.webp';
import iconCorporate from '../assets/coverage/icon-corporate.svg';
import iconPharma from '../assets/coverage/icon-pharma.svg';
import iconEnvironment from '../assets/coverage/icon-environment.svg';
import iconFinance from '../assets/coverage/icon-finance.svg';

const COVERAGE_CATEGORIES: {
  key: CoverageCategoryKey | 'corporate';
  title: string;
  subtitle: string;
  icon: string;
  included?: boolean;
}[] = [
  {
    key: 'corporate',
    title: 'Corporate',
    subtitle: 'Class actions, consumer fraud, and data breaches',
    icon: iconCorporate,
    included: true,
  },
  {
    key: 'pharma',
    title: 'Health & Pharmaceuticals',
    subtitle: 'Recalled medications, faulty medical devices, and harmful supplements',
    icon: iconPharma,
  },
  {
    key: 'environment',
    title: 'Environment & Exposure',
    subtitle: 'PFAS, Water contamination, toxic exposure',
    icon: iconEnvironment,
  },
  {
    key: 'finance',
    title: 'Finance & Banking',
    subtitle: 'Hidden fees, mortgage overcharges, and predatory lending practices',
    icon: iconFinance,
  },
];

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

function formatPayout(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

interface CoveragePayoutCardProps {
  extraCount: number;
  selected: Set<CoverageCategoryKey>;
  available: CoverageCategoryKey[];
  onToggle: (key: CoverageCategoryKey) => void;
}

export function CoveragePayoutCard({
  extraCount,
  selected,
  available,
  onToggle,
}: CoveragePayoutCardProps) {
  const metrics = coverageMetrics(extraCount);

  return (
    <div
      className="flex w-full flex-col overflow-hidden rounded-[24px]"
      style={{
        backgroundImage: 'linear-gradient(262.54deg, rgba(17, 170, 89, 0.2) 0%, rgba(107, 206, 62, 0.2) 100%)',
      }}
    >
      <div className="relative h-[91px] w-full overflow-hidden">
        <img
          src={coins}
          alt=""
          className="pointer-events-none absolute max-w-none object-cover"
          style={{
            height: '257.7%',
            left: '-9.14%',
            top: '-131.87%',
            width: '116.34%',
          }}
        />
      </div>

      <div className="flex flex-col gap-4 px-4 pb-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <p className="font-body text-xs font-normal leading-4 text-mf-neutral-900/60">Lawsuits</p>
              <p className="font-body text-base font-bold leading-5 text-mf-neutral-900">
                {formatCount(metrics.lawsuits)} / {formatCount(LAWSUITS_MAX)}
              </p>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <p className="font-body text-xs font-normal leading-4 text-mf-neutral-900/60">Potential Payout</p>
              <p className="whitespace-nowrap font-display text-[22px] font-bold leading-7 text-mf-neutral-900">
                {formatPayout(metrics.payout)} / {formatPayout(PAYOUT_MAX)}
              </p>
            </div>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-white/50">
            <div
              className="h-full rounded-full transition-[width,background-color] duration-300"
              style={{
                width: `${Math.max(8, metrics.lawsuitsRatio * 100)}%`,
                backgroundColor: metrics.barColor,
              }}
            />
          </div>
        </div>

        <div data-coverage-categories className="flex flex-col gap-2">
          {COVERAGE_CATEGORIES.filter(
            (cat) => cat.included || available.includes(cat.key as CoverageCategoryKey),
          ).map((cat) => {
            const isIncluded = Boolean(cat.included);
            const isOn = isIncluded || selected.has(cat.key as CoverageCategoryKey);
            return (
              <button
                key={cat.key}
                type="button"
                disabled={isIncluded}
                onClick={() => {
                  if (!isIncluded) onToggle(cat.key as CoverageCategoryKey);
                }}
                className="flex w-full items-center gap-3 overflow-hidden rounded-xl bg-white p-3 text-left shadow-[0_0_16px_rgba(26,22,19,0.03)]"
              >
                <span
                  className={cn(
                    'flex size-12 shrink-0 items-center justify-center rounded-[26px] p-3',
                    isIncluded ? 'bg-[#844fe1]/10' : 'bg-mf-green/10',
                  )}
                >
                  <img src={cat.icon} alt="" className="size-6 object-contain" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex flex-wrap items-center gap-1">
                    <span className="font-body text-[15px] font-bold leading-5 text-mf-neutral-900">
                      {cat.title}
                    </span>
                    {isIncluded && (
                      <span className="rounded-full bg-[#844fe1]/10 px-2 py-0.5 font-body text-[10px] font-medium leading-3 text-[#844fe1]">
                        Included with Base Plan
                      </span>
                    )}
                  </span>
                  <span className="font-body text-xs font-normal leading-4 text-mf-neutral-400">{cat.subtitle}</span>
                </span>
                <span
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-full',
                    isIncluded
                      ? 'bg-mf-green/20'
                      : isOn
                        ? 'bg-mf-green'
                        : 'border-2 border-mf-neutral-200 bg-white',
                  )}
                >
                  {(isIncluded || isOn) && <Check className="size-3.5 text-white" strokeWidth={1.5} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
